import React from 'react';
import { Card, Field, TextField, CssColorField, SaveButton, useSection, AnchorField } from '../AdminUI';
import { Plus, Trash2 } from 'lucide-react';
import MediaUpload from '../MediaUpload';

export default function GameCardsSection() {
  const [local, setLocal, save] = useSection('gameCards');
  const update = (i, k, v) => setLocal(local.map((s, idx) => idx === i ? { ...s, [k]: v } : s));
  const remove = (i) => setLocal(local.filter((_, idx) => idx !== i));
  const add = () => setLocal([
    ...local,
    {
      id: `gc_${Date.now()}`,
      label: 'New Service',
      sub: 'Description',
      icon: 'Sparkles',
      gradient: 'linear-gradient(90deg, #444 0%, #111 100%)',
      link: '#',
      image: '',
      detailVideo: '',
      detailText: '',
      contactLink: '',
      price: '',
    },
  ]);

  return (
    <Card
      title="Service Category Cards"
      desc="Click on a card on the homepage opens a popup. Each card below configures both the card AND its popup contents (image / video / details / contact / price). Fully dynamic."
      footer={<SaveButton onClick={save} />}
    >
      <div className="space-y-4">
        {(local || []).map((s, i) => (
          <div key={s.id} className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3" data-testid={`gc-row-${s.id}`}>
            <AnchorField anchor={`service-${s.id}`} label="Service-card anchor (deep-link)" />
            <div className="flex items-center gap-3 flex-wrap mt-2">
              <div className="hidden md:block w-32 h-14 rounded-md shrink-0" style={{ background: s.gradient }} />
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 flex-1 min-w-0">
                <Field label="Label"><TextField value={s.label} onChange={(v) => update(i, 'label', v)} /></Field>
                <Field label="Subtitle"><TextField value={s.sub ?? ''} onChange={(v) => update(i, 'sub', v)} /></Field>
                <Field label="Icon (fallback)"><TextField value={s.icon} onChange={(v) => update(i, 'icon', v)} /></Field>
                <Field label="Sale Price (₹)" hint="0 = hide Buy"><TextField value={s.price ?? ''} onChange={(v) => update(i, 'price', v)} placeholder="0" /></Field>
                <Field label="Gradient" hint="card background"><CssColorField value={s.gradient} onChange={(v) => update(i, 'gradient', v)} placeholder="linear-gradient(...)" /></Field>
                <Field label="Card Link" hint="legacy / unused since popup is the action"><TextField value={s.link ?? ''} onChange={(v) => update(i, 'link', v)} /></Field>
              </div>
              <button onClick={() => remove(i)} className="h-9 px-2 rounded-md bg-red-600/15 text-red-300 border border-red-900/40 flex items-center gap-1" data-testid={`gc-remove-${s.id}`}>
                <Trash2 size={14} />
              </button>
            </div>

            {/* Popup configuration */}
            <div className="mt-3 pt-3 border-t border-zinc-800">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--gold)]/70 font-bold mb-2">Popup Contents</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Card / popup image" hint="Shown on the card avatar AND in the popup banner">
                  <MediaUpload
                    value={s.image || ''}
                    onChange={(v) => update(i, 'image', v)}
                    accept="image"
                    previewClass="h-20 w-32"
                    testid={`gc-image-${s.id}`}
                  />
                </Field>
                <Field label="Popup video (optional)" hint="If set, plays inside the popup instead of the image">
                  <MediaUpload
                    value={s.detailVideo || ''}
                    onChange={(v) => update(i, 'detailVideo', v)}
                    accept="media"
                    previewClass="h-20 w-32"
                    testid={`gc-video-${s.id}`}
                  />
                </Field>
                <Field label="Contact link" hint="WhatsApp wa.me URL, tel:, mailto:, or any link">
                  <TextField
                    value={s.contactLink ?? ''}
                    onChange={(v) => update(i, 'contactLink', v)}
                    placeholder="https://wa.me/919..."
                  />
                </Field>
                <div className="md:col-span-1" />
                <div className="md:col-span-2">
                  <Field label="Details (popup body)" hint="Plain text — line breaks preserved. Tell users what's included, pricing notes, FAQs, etc.">
                    <textarea
                      value={s.detailText ?? ''}
                      onChange={(e) => update(i, 'detailText', e.target.value)}
                      placeholder="Write a rich description for the popup…"
                      className="w-full min-h-[100px] max-h-[280px] bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-sm text-zinc-100"
                      data-testid={`gc-detail-text-${s.id}`}
                    />
                  </Field>
                </div>
              </div>
            </div>
          </div>
        ))}
        <button onClick={add} className="w-full h-10 rounded-md border border-dashed border-zinc-700 hover:border-[color:var(--gold)]/50 text-zinc-300 text-sm font-semibold flex items-center justify-center gap-1.5" data-testid="gc-add-btn">
          <Plus size={14} /> Add Card
        </button>
      </div>
    </Card>
  );
}
